import React, { useState } from 'react';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';
import { MAIN_THEME } from '@styles';
import { isEmpty } from '@utils/validationUtils';
import { IFormFieldInputBaseProps } from '.';
import FormFieldWrapper from './FormFieldWrapper';
import HelpIcon from '@mui/icons-material/Help';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export interface IPasswordFormFieldProps extends IFormFieldInputBaseProps {
  value: string;
  loader?: boolean;
  hideError?: boolean;
  hideHint?: boolean;
  strongValidation?: boolean;
  classes?: {
    labelWrapper?: string;
    label?: string;
    input?: string;
  };
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const useStyles = makeStyles()(() => ({
  input: {
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '15px',
    height: 35,
    width: '100%',
    padding: '0 15px',
    margin: '0 auto',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white86}`,
    boxSizing: 'border-box',
    borderRadius: '5px',
    '&:disabled': {
      cursor: 'not-allowed',
      color: 'rgba(0, 0, 0, 0.38)!important',
    },
    '&::placeholder': {
      fontStyle: 'italic',
      fontFamily: MAIN_THEME.font.regular,
      color: MAIN_THEME.color.white86,
      opacity: 1,
      fontSize: MAIN_THEME.fontSize.sm,
    },
    fontFamily: MAIN_THEME.font.regular,
    '&:focus': {
      outline: 'none',
    },
    '::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  },
  inputError: {
    borderColor: `${MAIN_THEME.color.red} !important`,
    outlineColor: MAIN_THEME.color.red,
  },
  inputContainer: {
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    top: 'calc(50% - 10px)',
    right: '3%',
    opacity: 1,
    height: '25px',
    width: '25px',
  },
  materialInput: {
    backgroundColor: 'unset',
    border: 'none',
    borderBottom: `1px solid ${MAIN_THEME.color.white86}`,
    color: MAIN_THEME.color.black,
    margin: '0 auto',
    borderRadius: 0,
    padding: '10px 15px 4px 4px',
    fontSize: `${MAIN_THEME.fontSize.sm}`,
    MozAppearance: 'textfield',
  },
  disabledInput: {
    '& .MuiInputBase-root.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.5)!important',
      WebkitTextFillColor: 'rgba(0, 0, 0, 0.5)!important',
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.5)!important',
      WebkitTextFillColor: 'rgba(0, 0, 0, 0.5)!important',
    },
  },
  eyeIcon: {
    color: MAIN_THEME.color.black47,
    '& .MuiSvgIcon-root': {
      width: '0.7em',
      height: '0.7em',
    },
  },
  helpIcon: {
    color: MAIN_THEME.color.black47,
    marginLeft: '10px',
    width: '18px',
    height: '18px',
  },
  iconsContainer: {
    position: 'absolute',
    top: '50%',
    right: 10,
    paddingRight: '5px',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
  },
  tooltip: {
    backgroundColor: `${MAIN_THEME.color.white98} !important`,
    color: MAIN_THEME.color.black47,
    maxWidth: '220px!important',
    fontSize: MAIN_THEME.fontSize.xs,
    border: `1px solid ${MAIN_THEME.color.white86} !important`,
  },
}));

function getPasswordErrorMessage(value: string) {
  if (isEmpty(value)) {
    // If it's empty, we return no error.
    return '';
  }
  const passwordRegex =
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[+=_#?!@$%^&*-]).{8,}$/;
  if (!passwordRegex.test(value)) {
    return 'Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, 1 symbol, and be at least 8 characters long.';
  }
  return '';
}

const PasswordFormField: React.FC<IPasswordFormFieldProps> = ({
  error,
  name,
  title,
  value = '',
  placeholder,
  disabled,
  material,
  loader,
  inputOnly,
  hideError,
  hideHint = false,
  fullWidth,
  strongValidation,
  classes = {},
  onChange,
  onFocus,
  onBlur,
}) => {
  const { classes: inputClasses } = useStyles();

  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState<string>();

  const renderPasswordTooltipBody = () => (
    <div>
      <div>A minimum of 8 characters</div>
      <div>At least 1 upper case</div>
      <div>At least 1 lower case</div>
      <div>At least 1 number</div>
      <div>At least 1 symbol {`(@#$%&+=_)`}</div>
    </div>
  );

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (strongValidation) {
      setValidationError(getPasswordErrorMessage(value));
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  const renderInput = () => (
    <div className={inputClasses.inputContainer}>
      <input
        className={clsx(inputClasses.input, {
          [inputClasses.materialInput]: material,
          [inputClasses.inputError]: error || validationError,
          [inputClasses.disabledInput]: disabled,
          [classes.input]: classes.input,
        })}
        title={disabled && !isEmpty(value) ? value.toString() : title}
        id={name}
        name={name}
        aria-invalid={!!error || !!validationError}
        aria-describedby={`errorMsg-${name}`}
        value={value}
        onChange={onChange}
        onBlur={handleBlur}
        onFocus={onFocus}
        type={showPassword ? 'text' : 'password'}
        disabled={disabled}
        placeholder={placeholder}
      />
      <div className={inputClasses.iconsContainer}>
        <IconButton
          className={inputClasses.eyeIcon}
          aria-label="toggle password visibility"
          onClick={() => setShowPassword(!showPassword)}
          edge="end"
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>

        {!hideHint && (
          <Tooltip
            title={renderPasswordTooltipBody()}
            classes={{ tooltip: inputClasses.tooltip }}
          >
            <HelpIcon
              className={inputClasses.helpIcon}
              width={18}
              height={18}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );

  return inputOnly ? (
    renderInput()
  ) : (
    <FormFieldWrapper
      title={title}
      error={validationError || error}
      name={name}
      fullWidth={fullWidth}
      className={classes?.labelWrapper}
      material={material}
      hideError={hideError}
    >
      {renderInput()}
      {loader && (
        <CircularProgress
          className={inputClasses.loader}
          size={20}
          thickness={3}
        />
      )}
    </FormFieldWrapper>
  );
};

export default PasswordFormField;
